/**
 * The topbar day chip — V6-04 §4.
 *
 * "One click, always visible; this is the whole daily interaction." Everything
 * else in this module is for the days something goes wrong; this is the days
 * it does not, so it has to be the least effortful thing on the screen.
 *
 * Ending asks for confirmation with an optional note, because ending is the
 * irreversible half — a mistaken start costs a click, a mistaken end costs a
 * correction request and somebody else's approval.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlarmClockCheck, Loader2, Play, Square } from 'lucide-react';
import { toast } from 'sonner';
import { ERROR_CODES } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAttendanceChip, useEndWork, useStartWork } from './api';
import { isStaleSession, openSinceLabel } from './staleSession';

/** Ticks the elapsed label without re-fetching. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function elapsedLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h ${rest}m` : `${rest}m`;
}

/** `19:04`, in the reader's own locale. */
function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function DayChip() {
  const { data: chip } = useAttendanceChip();
  const start = useStartWork();
  const end = useEndWork();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');

  const running = Boolean(chip?.open);
  const now = useNow(running);

  if (!chip?.enabled) return null;

  /**
   * V10 §1 — a session that outlived its own day.
   *
   * The chip used to derive everything from today's rows, so this state
   * rendered as "Start work" and the only click available answered with an
   * error and no remedy. Now it is a first-class state with its own label and
   * its own way out.
   */
  const stale = isStaleSession(chip.open, chip.today);
  const since = chip.open ? openSinceLabel(chip.open.workDate, chip.today) : null;

  // A stale session's wall-clock elapsed is seventeen hours of not being at a
  // desk, so it is never used as "today's total" — only today's own minutes.
  const openMinutes =
    chip.open && !stale
      ? Math.max(0, Math.floor((now - new Date(chip.open.startAt).getTime()) / 60_000))
      : 0;
  const totalMinutes = chip.todayMinutes + openMinutes;

  async function onStart(endPrevious = false): Promise<void> {
    try {
      await start.mutateAsync({ endPrevious });
      setConfirming(false);
      setNote('');
      toast.success(
        endPrevious
          ? 'Previous session closed — work started'
          : 'Work started — have a good day',
      );
    } catch (err) {
      // The dead end, closed: an open session is never just an error message,
      // it is an offer to deal with the session that is in the way.
      if (err instanceof ApiError && err.code === ERROR_CODES.ATTENDANCE_SESSION_OPEN) {
        setConfirming(true);
        toast.error(`${err.message}. Use “End previous session & start”.`);
        return;
      }
      toast.error(err instanceof ApiError ? err.message : 'Could not start your workday');
    }
  }

  async function onEnd(): Promise<void> {
    try {
      await end.mutateAsync(note.trim() || undefined);
      setConfirming(false);
      setNote('');
      toast.success(stale ? 'Previous session closed' : `Work ended — ${elapsedLabel(totalMinutes)} today`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not end your workday');
    }
  }

  return (
    <div
      className="relative"
      data-testid="day-chip"
      data-running={running ? 'true' : 'false'}
      data-stale={stale ? 'true' : 'false'}
    >
      {running ? (
        <Button
          variant="subtle"
          size="sm"
          data-testid="end-work"
          onClick={() => setConfirming((value) => !value)}
        >
          <span
            className="size-1.5 rounded-full bg-[var(--accent)] motion-safe:animate-pulse"
            aria-hidden
          />
          {stale && since && chip.open ? (
            <span data-testid="day-chip-since">
              Working since {since} {clockLabel(chip.open.startAt)}
            </span>
          ) : (
            <span className="vt-mono" data-testid="day-chip-elapsed">
              {elapsedLabel(totalMinutes)}
            </span>
          )}
          <span className="hidden sm:inline">End work</span>
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          data-testid="start-work"
          disabled={start.isPending}
          onClick={() => void onStart()}
        >
          {start.isPending ? <Loader2 className="animate-spin" /> : <Play />}
          <span className="hidden sm:inline">
            {chip.todayMinutes > 0 ? `Start again · ${elapsedLabel(totalMinutes)}` : 'Start work'}
          </span>
        </Button>
      )}

      {confirming && (
        <div
          className="vt-surface vt-elevated absolute right-0 top-9 z-50 flex w-[280px] flex-col gap-2 p-2"
          data-testid="end-work-confirm"
        >
          {stale && chip.open ? (
            <p className="text-[12px]">
              A session from {chip.open.workDate} is still running.{' '}
              <span className="vt-muted">
                Ending it books the day up to the auto-close cut-off and flags it, so you can
                request a correction if that is wrong.
              </span>
            </p>
          ) : (
            <p className="text-[12px]">
              End your workday?{' '}
              <span className="vt-muted">{elapsedLabel(totalMinutes)} recorded.</span>
            </p>
          )}
          <Input
            data-testid="end-work-note"
            placeholder="Note (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex flex-wrap gap-1.5">
            {stale && (
              <Button
                variant="primary"
                size="sm"
                data-testid="end-previous-and-start"
                disabled={start.isPending}
                onClick={() => void onStart(true)}
              >
                {start.isPending ? <Loader2 className="animate-spin" /> : <Play className="size-3" />}
                End previous session &amp; start
              </Button>
            )}
            <Button
              variant={stale ? 'subtle' : 'primary'}
              size="sm"
              data-testid="end-work-confirm-yes"
              disabled={end.isPending}
              onClick={() => void onEnd()}
            >
              {end.isPending ? <Loader2 className="animate-spin" /> : <Square className="size-3" />}
              {stale ? 'Just end it' : 'End work'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Yesterday auto-closed at 19:32 — request a correction if that's wrong."
 *
 * Under the topbar rather than inside the chip: it is about a different day,
 * and it must survive the person clicking start on this one.
 */
export function AutoCloseBanner() {
  const { data: chip } = useAttendanceChip();
  if (!chip?.enabled || !chip.autoClosed) return null;

  const at = chip.autoClosed.lastOut
    ? new Date(chip.autoClosed.lastOut).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'the cut-off';

  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b border-[var(--border)] px-3 py-1.5 text-[12px]',
        'bg-[var(--warn-soft,var(--surface-2))]',
      )}
      role="status"
      data-testid="auto-close-banner"
      data-date={chip.autoClosed.date}
    >
      <AlarmClockCheck className="size-3.5 shrink-0 text-[var(--warn)]" />
      <span className="min-w-0 flex-1 truncate">
        {chip.autoClosed.date} was closed automatically at {at} — request a correction if that is
        wrong.
      </span>
      <Link
        to={`/attendance?correct=${chip.autoClosed.date}`}
        className="shrink-0 font-medium text-[var(--accent)] hover:underline"
        data-testid="auto-close-banner-fix"
      >
        Request a correction
      </Link>
    </div>
  );
}
